<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import { Schedule } from '@/components/Schedule/Schedule'
import AppState, {
  recurringAppointmentPaymentOptions,
  setEditCalendarValidationData,
} from '@/states/app'
import calendarScheduleState from '@/states/calendarScheduleState'
import { computedTimezones } from '@/utils/timezonesOptions'
import {
  ClockIcon,
  Copy06Icon,
  Edit01Icon,
  InfoCircleIcon,
  PlusIcon,
  SearchSmIcon,
  Trash02Icon,
  XIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICheckbox,
  UIDivider,
  UIDropdown,
  UIForm,
  UIFormItem,
  UIHeader,
  UIInputNumber,
  UIPopover,
  UISelect,
  UISkeleton,
  UISwitch,
  UITimePicker,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { debounce } from 'lodash'
import moment from 'moment'
import { computed, h, reactive, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { POSITION, TYPE, useToast } from 'vue-toastification'
import { MyAvailability } from '../../class/pages/MyAvailability'
import { CalendarService } from '../../class/services/CalendarService'
import {
  CalendarType,
  RecurringAppointmentsPaymentType,
} from '../../models/calendar'
import {
  getInitials,
  getTimeSpanOptions,
  getTooltip,
} from '../../utils/generalFunctions'
import DependentSettings from '../common/DependentSettings.vue'
import { useAvailabilityText } from '../Schedule/ScheduleHelper'
import CustomAvailabilityModal from '../Settings/CustomAvailabilityModal.vue'
import CustomRecurrenceModal from './CustomRecurrenceModal.vue'
import DurationPicker from './DurationPicker.vue'
import TeamMemberAvatarGroup from './TeamMemberAvatarGroup.vue'
import TeamMemberSchedulePanel from './TeamMemberSchedulePanel.vue'

const myAvailability = ref(new MyAvailability(''))
const copyToDays = ref<string[]>([])
const activeHourIndexForCopy = ref(-1)
const customAvailabilityPicker = ref()
const formRef = ref()
const { t } = useI18n()
const getAvailabilityText = useAvailabilityText()
const daysOfWeekInOrder = [
  t('settings.availability.sunday'),
  t('settings.availability.monday'),
  t('settings.availability.tuesday'),
  t('settings.availability.wednesday'),
  t('settings.availability.thursday'),
  t('settings.availability.friday'),
  t('settings.availability.saturday'),
]
const toast = useToast()

const recurringFrequencyOptions = [
  {
    value: 'DAILY',
    label: t('calendar_advanced.availability.daily'),
  },
  {
    value: 'WEEKLY',
    label: t('calendar_advanced.availability.weekly'),
  },
  {
    value: 'MONTHLY',
    label: t('calendar_advanced.availability.monthly'),
  },
  {
    value: 'CUSTOM',
    label: t('calendar_advanced.availability.custom'),
  },
]

const recurringBookingOptions = [
  {
    value: 'skip',
    label: t('calendar_advanced.availability.skip_booking'),
  },
  {
    value: 'continue',
    label: t('calendar_advanced.availability.force_book'),
  },
  {
    value: 'book_next',
    label: t('calendar_advanced.availability.book_next_available'),
  },
]

const recurringOverlapStatusOptions = [
  {
    value: 'confirmed',
    label: t('calendar_advanced.availability.confirmed'),
  },
  {
    value: 'new',
    label: t('calendar_advanced.availability.unconfirmed'),
  },
]

const state = reactive({
  isLoading: false,
  // isStandardAvailability: true,
  isStandardAvailability: false,
  oldFreqValue: '',
  isCustomRecurring: false,
  rruleText: '',
  formData: {
    openHours: [],
    enableRecurring: false,
    recurring: {
      count: 1,
      bookingOption: 'skip',
      bookingOverlapDefaultStatus: '',
      interval: null as null | number,
      freq: 'DAILY',
      monthDays: [],
      weekDays: [],
    },
    slotDuration: '30',
    slotDurationUnit: 'mins',
    slotInterval: '30',
    slotIntervalUnit: 'mins',
    slotBuffer: '',
    slotBufferUnit: 'mins',
    allowBookingFor: '',
    allowBookingForUnit: 'days',
    allowBookingAfter: '',
    allowBookingAfterUnit: 'days',
    appoinmentPerSlot: 1 as number | undefined,
    appoinmentPerDay: 1 as number | undefined,
    preBuffer: '',
    preBufferUnit: 'mins',
    lookBusyConfig: {
      enabled: false,
      lookBusyPercentage: 0,
    },
  },
  showCustomRecurrenceModal: false,
  isInitialDataLoaded: false,
  showDependentSettings: false,
  recurrTeamMembers: undefined,
  recurringPaymentType: RecurringAppointmentsPaymentType.FirstAppointmentOnly,
})
// this is the default schedule for the team members
// team member Id and schedule will be stored in this object
const teamMembersDefaultSchedule = ref<{ [key: string]: any }>({})

const formattedTimezone = computed(() => {
  const timezone = calendarSchedule.value.timezone || AppState?.locationTimezone
  if (!timezone) return ''

  const momentTz = moment.tz(timezone)
  const offset = momentTz.format('Z')
  const abbreviation = momentTz.format('z')

  return `GMT${offset} ${timezone} (${abbreviation})`
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const emit = defineEmits(['onSave', 'onCancel', 'save-calendar'])

const methods = {
  async loadData() {
    state.isInitialDataLoaded = true

    if (editingCalendar.value?.calendarType === CalendarType.EVENT) {
      // Check if this is a new calendar from draft (no ID yet)
      if (!editingCalendar.value?.id) {
        // Use draft data openHours directly, don't try to load from API
        myAvailability.value.setOpenHoursDataFromDatabase(
          editingCalendar.value?.openHours &&
            Array.isArray(editingCalendar.value?.openHours)
            ? editingCalendar.value?.openHours
            : []
        )
        eventCalendarTimezone.value = AppState.locationTimezone || ''
      } else {
        //load open hours from schedule for existing calendar
        const eventCalendarSchedule =
          await calendarScheduleState.getEventCalendarSchedules(
            editingCalendar.value?.id
          )
        const openHours = eventCalendarSchedule.getOpenHours()
        myAvailability.value.setOpenHoursDataFromDatabase(openHours)
        eventCalendarTimezone.value =
          eventCalendarSchedule.timezone || AppState.locationTimezone || ''
      }
    } else {
      myAvailability.value.setOpenHoursDataFromDatabase(
        editingCalendar.value?.openHours &&
          Array.isArray(editingCalendar?.value?.openHours)
          ? editingCalendar.value?.openHours // {}
          : []
      )
    }

    state.isStandardAvailability = editingCalendar.value?.availabilityType !== 1
    state.formData.recurring =
      editingCalendar.value?.recurring ||
      ({
        count: 1,
        bookingOption: 'skip',
        bookingOverlapDefaultStatus: '',
        interval: null as null | number,
        freq: 'DAILY',
        monthDays: [],
        weekDays: [],
      } as any)

    state.formData.recurring.bookingOption =
      editingCalendar.value?.recurring?.bookingOption || 'skip'

    state.formData.recurring.freq =
      editingCalendar.value?.recurring?.freq || 'DAILY'

    state.formData.recurring.count =
      editingCalendar.value?.recurring?.count || 1

    if (
      state.formData.recurring?.monthDays?.length > 0 ||
      state.formData.recurring?.weekDays?.length > 0 ||
      state.formData.recurring?.interval
    ) {
      state.isCustomRecurring = true
    }

    state.formData.enableRecurring = editingCalendar.value
      ?.enableRecurring as boolean

    state.formData.slotDuration =
      editingCalendar.value?.slotDurationUnit === 'hours'
        ? String(editingCalendar.value?.slotDuration / 60 || '')
        : String(editingCalendar.value?.slotDuration || '')
    state.formData.slotDurationUnit =
      editingCalendar.value?.slotDurationUnit || 'mins'
    state.formData.slotInterval =
      editingCalendar.value?.slotIntervalUnit === 'hours'
        ? String(editingCalendar.value?.slotInterval / 60 || '')
        : String(editingCalendar.value?.slotInterval || '')
    state.formData.slotIntervalUnit =
      editingCalendar.value?.slotIntervalUnit || 'mins'
    state.formData.slotBuffer =
      editingCalendar.value?.slotBufferUnit === 'hours'
        ? String(editingCalendar.value?.slotBuffer / 60 || '')
        : String(editingCalendar.value?.slotBuffer || '')
    state.formData.slotBufferUnit =
      editingCalendar.value?.slotBufferUnit || 'mins'
    state.formData.preBuffer =
      editingCalendar.value?.preBufferUnit === 'hours'
        ? String(editingCalendar.value?.preBuffer / 60 || '')
        : String(editingCalendar.value?.preBuffer || '')
    state.formData.preBufferUnit =
      editingCalendar.value?.preBufferUnit || 'mins'
    state.formData.allowBookingFor = String(
      editingCalendar.value?.allowBookingFor || ''
    )
    state.formData.allowBookingForUnit =
      editingCalendar.value?.allowBookingForUnit || 'days'
    state.formData.allowBookingAfter = String(
      editingCalendar.value?.allowBookingAfter || ''
    )
    state.formData.allowBookingAfterUnit =
      editingCalendar.value?.allowBookingAfterUnit || 'days'
    state.formData.appoinmentPerSlot = editingCalendar.value?.appoinmentPerSlot
    state.formData.appoinmentPerDay = editingCalendar.value?.appoinmentPerDay

    state.formData.lookBusyConfig = editingCalendar.value?.lookBusyConfig || {
      enabled: false,
      lookBusyPercentage: 0,
    }
    if (state.formData.enableRecurring) {
      await getRRuleText()
    }
  },
  async emitSave() {
    if (state.isStandardAvailability) {
      const hasDuplicateSlot = myAvailability.value.hasDuplicateSlot
      let has24hourError = false
      myAvailability.value.availableDays.forEach(day => {
        if (day.hours.length > 1) {
          if (
            day.hours.some(
              hour => hour.startTime === '0:0' && hour.endTime === '0:0'
            )
          ) {
            has24hourError = true
          }
        }
      })
      if (hasDuplicateSlot || has24hourError) {
        toast(t('calendar_advanced.availability.enter_valid_hours'), {
          type: TYPE.ERROR,
          position: POSITION.BOTTOM_RIGHT,
        })
        return null
      }
    }
    if (customAvailabilityPicker.value?.methods.hasErrors()) {
      toast(t('calendar_advanced.availability.enter_valid_avail'), {
        type: TYPE.ERROR,
        position: POSITION.BOTTOM_RIGHT,
      })
      return null
    }

    try {
      await formRef.value.getForm().validate()
    } catch (error) {
      return
    }
    if (
      AppState.errorInDurationPickerMap.slotDuration ||
      AppState.errorInDurationPickerMap.slotInterval ||
      AppState.errorInDurationPickerMap.slotBuffer ||
      AppState.errorInDurationPickerMap.preBuffer
    ) {
      return null
    }

    const data = {
      openHours: myAvailability.value.availableDays
        ?.filter(i => i?.hours?.length)
        .map(day => day.serializeAsPerAPIFormat()),
      availabilities:
        customAvailabilityPicker.value?.methods.getAvailability() || [],
      // availabilityType: 1,
      // availabilityType: 0,
      recurring: state.formData.recurring,
      enableRecurring: state.formData.enableRecurring,
      slotDuration:
        state.formData.slotDurationUnit === 'mins'
          ? parseInt(state.formData.slotDuration)
          : parseInt(state.formData.slotDuration) * 60,
      slotDurationUnit: state.formData.slotDurationUnit,
      slotInterval:
        state.formData.slotIntervalUnit === 'mins'
          ? parseInt(state.formData.slotInterval)
          : parseInt(state.formData.slotInterval) * 60,
      slotIntervalUnit: state.formData.slotIntervalUnit,
      slotBuffer:
        state.formData.slotBufferUnit === 'mins'
          ? parseInt(state.formData.slotBuffer)
          : parseInt(state.formData.slotBuffer) * 60,
      slotBufferUnit: state.formData.slotBufferUnit,
      preBuffer:
        state.formData.preBufferUnit === 'mins'
          ? parseInt(state.formData.preBuffer)
          : parseInt(state.formData.preBuffer) * 60,
      preBufferUnit: state.formData.preBufferUnit,
      allowBookingFor: parseInt(state.formData.allowBookingFor),
      allowBookingForUnit: state.formData.allowBookingForUnit,
      allowBookingAfter: parseInt(state.formData.allowBookingAfter),
      allowBookingAfterUnit: state.formData.allowBookingAfterUnit,
      appoinmentPerSlot: state.formData.appoinmentPerSlot
        ? Number(state.formData.appoinmentPerSlot)
        : '',
      appoinmentPerDay: state.formData.appoinmentPerDay
        ? Number(state.formData.appoinmentPerDay)
        : '',
      lookBusyConfig: state.formData.lookBusyConfig,
    } as any
    return data
  },
  updateFrequency(value: string) {
    if (value === 'CUSTOM') {
      state.oldFreqValue = state.formData.recurring.freq
      state.showCustomRecurrenceModal = true
    } else {
      state.isCustomRecurring = false
      state.formData.recurring.monthDays = []
      state.formData.recurring.weekDays = []
      state.formData.recurring.interval = null
      state.formData.recurring.freq = value
    }
  },
  customRecurrenceModalSave(data: any) {
    state.isCustomRecurring = true
    state.formData.recurring = {
      ...state.formData.recurring,
      ...data,
    }
    state.showCustomRecurrenceModal = false
  },
  customRecurrenceModalCancel() {
    state.showCustomRecurrenceModal = false
    state.formData.recurring.freq = state.oldFreqValue
  },
  updateSelectedSchedules(schedule: any, teamMemberIds: string[]) {
    const availabilityText = getAvailabilityText(schedule)
    teamMemberIds.forEach(teamMemberId => {
      teamMembersDefaultSchedule.value[teamMemberId] = {
        availabilityText,
        schedule,
      }
    })
  },
}

defineExpose({
  methods,
  getSelectedEventTimezone: () => selectedEventTimezone.value,
})

watch(
  () => state.formData.recurring,
  () => {
    if (!state.isInitialDataLoaded) {
      getRRuleText()
    }
  },
  {
    deep: true,
  }
)

const getRRuleText = async () => {
  const { data } = await CalendarService.generateRRuleText({
    freq: state.formData.recurring.freq,
    interval: state.formData.recurring.interval,
    count: state.formData.recurring.count,
    weekDays: state.formData.recurring.weekDays,
    monthDays: state.formData.recurring.monthDays,
  })

  state.rruleText = data
}

const rules = {
  appoinmentPerSlot: {
    validator() {
      if (editingCalendar.value?.calendarType === CalendarType.CLASS_BOOKING) {
        if (!state.formData.appoinmentPerSlot) {
          return new Error(t('settings.availability.schedule.this_is_required'))
        }

        if (state.formData.appoinmentPerSlot > 9999) {
          return new Error('This cannot be greater than 9999')
        }
      }

      if (
        state.formData.appoinmentPerSlot &&
        state.formData.appoinmentPerSlot < 0
      ) {
        return new Error('This cannot be less than 0')
      }

      return true
    },
    trigger: ['input', 'blur'],
  },
  recurringCount: {
    validator() {
      if (state.formData.enableRecurring) {
        if (state.formData.recurring.count > 24) {
          return new Error('Count cannot be greater than 24')
        } else if (state.formData.recurring.count < 1) {
          return new Error('Count cannot be less than 1')
        }
      }

      return true
    },
    trigger: ['input', 'blur'],
  },
  appoinmentPerDay: {
    validator() {
      if (
        state.formData.appoinmentPerDay &&
        state.formData.appoinmentPerDay < 0
      ) {
        return new Error('This cannot be less than 0')
      }
      return true
    },
    trigger: ['input', 'blur'],
  },
}

watchEffect(() => {
  setEditCalendarValidationData(
    'enableRecurring',
    state.formData.enableRecurring
  )
})

watch(
  () => calendarScheduleState.state.schedules,
  schedules => {
    if (schedules && Object.keys(schedules).length > 0) {
      getAvailabilityTextFromSchedule()
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)

watch(
  () => myAvailability.value.getAvailableDaysCount(),
  () => {
    if (isAvailableDaysCountZero.value) {
      state.formData.enableRecurring = false
    }
  }
)
const calendarSchedule = computed(() => {
  return (
    calendarScheduleState?.state?.eventCalendarSchedule[
      editingCalendar?.value?.id || ''
    ] || new Schedule({})
  )
})

const eventCalendarTimezone = ref<string>('')

const selectedEventTimezone = computed({
  get() {
    return (
      eventCalendarTimezone.value ||
      calendarSchedule.value.timezone ||
      AppState.locationTimezone ||
      ''
    )
  },
  set(value: string) {
    eventCalendarTimezone.value = value
    // Also update the schedule object if it exists
    if (calendarSchedule.value) {
      calendarSchedule.value.timezone = value
    }
  },
})

const updateEventCalendarTimezone = (value: string) => {
  eventCalendarTimezone.value = value
  if (calendarSchedule.value) {
    calendarSchedule.value.setTimezone(value)
  }
}

const isAvailableDaysCountZero = computed(() => {
  if (editingCalendar.value?.calendarType === CalendarType.EVENT) {
    return myAvailability.value.getAvailableDaysCount() === 0
  }
  return false
})

const handleIsHourDisabled = (hour: number, startTime?: string) => {
  const startHour = moment(startTime, 'H:m').hour()
  if (hour === 0) {
    return false
  }
  if (hour < startHour) {
    return true
  }
  return false
}

const handleIsMinDisabled = (
  min: number,
  hour: number | null,
  startTime?: string
) => {
  const startMin = moment(startTime, 'H:m').minute()
  const startHour = moment(startTime, 'H:m').hour()
  if (hour === 0) {
    return false
  }
  if (hour === startHour && min <= startMin) {
    return true
  }
  return false
}
const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const isCollectiveCalendar = computed(() => {
  return editingCalendar.value?.isCollectiveCalendar
})

const bufferOptionForServices = computed(() => {
  const MAX_BUFFER = 90
  return getTimeSpanOptions(MAX_BUFFER, 0, 15)
})
const isRecurringMeetingSwitchDisabled = computed(() => {
  const userIds = (AppState.editCalendarValidationData.teamMembers || []).map(
    m => m.userId
  )
  const hasDateSpecificHours = userIds.some(
    userId =>
      calendarScheduleState.state.userSchedulesInfo[userId]
        ?.hasDateSpecificHours
  )

  return (
    (AppState.editCalendarValidationData?.teamMembers?.length ?? 0) > 1 ||
    hasDateSpecificHours
  )
})

const recurringMeetingSwitchTooltip = computed(() => {
  if ((AppState.editCalendarValidationData?.teamMembers?.length ?? 0) > 1) {
    return t(
      'settings.availability.schedule.recurring_meetings_enabled_multi_user'
    )
  }
  const userIds = (AppState.editCalendarValidationData.teamMembers || []).map(
    m => m.userId
  )
  const tooltip = getTooltipForEnablingRecurring(userIds)
  if (tooltip) {
    return tooltip
  }
  return ''
})
const getTooltipForEnablingRecurring = (userIds: string[]) => {
  for (const userId of userIds) {
    const info = calendarScheduleState.state.userSchedulesInfo[userId]
    if (info?.hasDateSpecificHours) {
      return t(
        'settings.availability.schedule.recurring_incompatible_with_date_hours'
      )
    }
  }
  return ''
}
const onRecurringMeetingSwitchChange = (value: boolean) => {
  if (value && shouldDisableRecurringMeetingSwitch.value) {
    state.showDependentSettings = true
  } else {
    state.showDependentSettings = false
    state.formData.enableRecurring = value
  }
}

const onClickConfirm = () => {
  if (AppState.editCalendarValidationData.teamMembers.length) {
    AppState.editCalendarValidationData.selectedTeamMember =
      state.recurrTeamMembers
  }
  if (AppState.shouldAcceptPayment && AppState.isPartialPaymentChecked) {
    AppState.editCalendarValidationData.recurringPaymentType =
      state.recurringPaymentType
  }
  customAvailabilityPicker.value?.methods?.resetCustomAvailability()
  AppState.isPartialPaymentChecked = false
  state.showDependentSettings = false
}

const onClickCancel = () => {
  state.showDependentSettings = false
  state.formData.enableRecurring = false
}

const shouldDisableRecurringMeetingSwitch = computed(() => {
  return (
    AppState.editCalendarValidationData?.teamMembers.length > 1 ||
    customAvailabilityPicker.value?.methods?.getAvailabilityLength() > 0 ||
    (AppState.shouldAcceptPayment && AppState.isPartialPaymentChecked)
  )
})

const recurringDependentSettings = computed(() => {
  const settings: string[] = []
  if (customAvailabilityPicker.value?.methods?.getAvailabilityLength() > 0) {
    settings.push(t('calendar_advanced.availability.date_specific_hours'))
  }
  if (AppState.shouldAcceptPayment && AppState.isPartialPaymentChecked) {
    settings.push(t('calendar_advanced.availability.partial_payments'))
  }
  if (AppState.editCalendarValidationData?.teamMembers.length > 1) {
    settings.push(t('calendar_advanced.availability.multiple_team_members'))
  }
  return settings
})

const usersInLocationOptions = computed(() => {
  const selectedTeamMembers =
    AppState.editCalendarValidationData?.teamMembers ?? []
  const selectedTeamMembersIds = new Set(
    selectedTeamMembers.map((x: any) => x.userId)
  )
  const { users } = AppState.globalUsers as any
  const teamMembers = users
    .filter((x: any) => selectedTeamMembersIds.has(x.id))
    .map((x: any) => ({ value: x.id, label: x.name }))
  const allTeamMembers = users
    .filter((x: any) => !selectedTeamMembersIds.has(x.id))
    .map((x: any) => ({ value: x.id, label: x.name }))

  const data = [
    {
      type: 'group',
      label: t('calendar_advanced.availability.team_members_in_this_calendar'),
      key: 'selected',
      children: teamMembers,
    },
    {
      type: 'group',
      label: t('calendar_advanced.availability.other_team_members'),
      key: 'not-selected',
      children: allTeamMembers,
    },
  ]
  return data
})

const dependentSettingsConfirmSaveButtonDisabled = computed(() => {
  return (
    AppState.editCalendarValidationData?.teamMembers.length > 1 &&
    !state.recurrTeamMembers
  )
})

const commonStyles = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
}

const renderLabel = (option, selected) => {
  return h(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        marginTop:
          option.type === 'group' && option.key === 'not-selected'
            ? '5px'
            : '0',
      },
    },
    [
      option.type === 'group' && option.key === 'not-selected'
        ? h('div', {
            style: {
              height: '1px',
              backgroundColor: 'var(--gray-300)',
              marginBottom: '8px',
              width: '100%',
              flexShrink: 0,
              alignSelf: 'stretch',
            },
          })
        : null,
      h(
        'div',
        {
          style: {
            ...commonStyles,
            width: '100%',
          },
          class: selected ? 'bg-primary' : '',
        },
        [
          h(
            'p',
            {
              style: {
                margin: 0,
                width: '100%',
              },
            },
            [option.label as string]
          ),
          option.description &&
            h(
              'span',
              {
                style: {
                  color: 'var(--gray-500)',
                },
              },
              option.description
            ),
        ]
      ),
    ]
  )
}

// Team member bar and side panel state
const showTeamMemberPanel = ref(false)

const teamMembersWithFullDetails = computed(() => {
  const teamMembers =
    AppState.editCalendarValidationData.teamMembers ||
    editingCalendar.value?.teamMembers ||
    []
  const users = (AppState.globalUsers as any)?.users || []
  return teamMembers
    .filter((x: any) => x.selected)
    .map((x: any) => {
      const user = users.find((y: any) => y.id === x.userId)
      return { ...x, ...user }
    })
})

const firstFourTeamMembers = computed(() => {
  const teamMembers =
    AppState.editCalendarValidationData.teamMembers ||
    editingCalendar.value?.teamMembers ||
    []
  const users = (AppState.globalUsers as any)?.users || []
  // Get the first 4 team members if length is greater than 4
  if (teamMembers.length > 4) {
    return teamMembers.slice(0, 4).map((x: any) => {
      const user = users.find((y: any) => y.id === x.userId)
      return { ...x, ...user }
    })
  }
  // If length is less than 4, return the team members
  return teamMembers
    .filter((x: any) => x.selected)
    .map((x: any) => {
      const user = users.find((y: any) => y.id === x.userId)
      return { ...x, ...user }
    })
})

const teamMembersWithFullDetailsMoreThan4 = computed(() => {
  const teamMembers =
    AppState.editCalendarValidationData.teamMembers ||
    editingCalendar.value?.teamMembers ||
    []
  const users = (AppState.globalUsers as any)?.users || []
  // Skip the first 4 team members
  const rest = teamMembers.slice(4)
  // Get the users for the rest of the team members
  const restUsers = rest.map((x: any) => {
    const user = users.find((y: any) => y.id === x.userId)
    return { ...x, ...user }
  })
  // Return the rest of the team members
  return restUsers
    .filter((x: any) => x.selected)
    .map((x: any) => {
      const user = users.find((y: any) => y.id === x.userId)
      return { ...x, ...user }
    })
})

function openTeamMemberPanel(member: any) {
  console.log('openTeamMemberPanel', member)
  bulkEditSelectedUsers.value = [member]
  showTeamMemberPanel.value = true
}
function closeTeamMemberPanel() {
  showTeamMemberPanel.value = false
  bulkEditSelectedUsers.value = []
  selectedUserIds.value = []
}

const showBulkEditDropdown = ref(false)
const bulkEditSelectedUsers = ref<any[]>([])
const usersOptions = ref<any[]>((AppState.globalUsers as any)?.users || [])

const allUsersOptions = computed(() => {
  const users = usersOptions.value
  return [
    {
      key: 'search',
      label: t('settings.availability.schedule.search'),
      value: 'search',
    },
    {
      label: t('settings.calendar.all'),
      value: 'all',
    },
    ...users
      .filter((x: any) =>
        (
          AppState.editCalendarValidationData.teamMembers ||
          editingCalendar.value?.teamMembers
        )?.find((y: any) => y.userId === x.id)
      )
      .map((user: any) => ({
        label: user.name,
        value: user.id,
        key: user.id,
        imageUrl: user.profilePhoto,
        description: user.email,
      })),
  ]
})

function openBulkEditDropdown() {
  showBulkEditDropdown.value = true
}
function closeBulkEditDropdown() {
  showBulkEditDropdown.value = false
}
const searchInputRef = ref()
const searchValue = ref('')
const handleCalendarSearch = debounce((text: string) => {
  searchValue.value = text
  usersOptions.value = (AppState.globalUsers as any)?.users.filter(user =>
    user.name.toLowerCase().includes(searchValue.value.toLowerCase())
  )
}, 300)

const SearchIconNode = h(
  'div',
  {
    class:
      'absolute inset-y-0 left-0 flex items-center gap-2 pl-4 text-gray-300 pointer-events-none',
  },
  [
    h(SearchSmIcon, {
      class: 'text-gray-300',
      height: '20',
      style: {
        width: '20px',
        height: '20px',
      },
    }),
  ]
)

const selectedUserIds = ref<string[]>([])

const handleOptionClick = (option: any) => {
  const isSelected = selectedUserIds.value.includes(option.value)
  // If the option is all, select all the options
  if (option.value === 'all') {
    selectedUserIds.value = isSelected
      ? []
      : allUsersOptions.value.map(x => x.value)
  } else {
    selectedUserIds.value = isSelected
      ? selectedUserIds.value.filter(x => x !== option.value)
      : [...selectedUserIds.value, option.value]
  }
  bulkEditSelectedUsers.value = selectedUserIds.value
    .map(x => {
      const user = teamMembersWithFullDetails.value.find(y => y.userId === x)
      return user ? { ...user, selected: true } : null
    })
    .filter(x => x !== null)
}

const handleEditHoursClick = () => {
  showTeamMemberPanel.value = true
}

/**
 * Render the option for the bulk edit dropdown
 * @param prop - The prop object
 * @returns The rendered option
 */
const renderOption = (prop: any) => {
  const option = prop.option
  const optionsArray = allUsersOptions.value
  const isLastOption =
    optionsArray[optionsArray.length - 1]?.value === option.value

  const SearchElement = h(
    'div',
    { class: 'sticky top-0 w-full p-1 z-10 bg-white pt-[5px]' },
    [
      SearchIconNode,
      h('input', {
        ref: searchInputRef,
        key: 'calendar-search-input',
        class: `w-full rounded-md border-2 border-gray-300 p-2 text-gray-700 text-sm font-normal !pl-10`,
        placeholder: t(
          'settings.availability.schedule.select_users_placeholder'
        ),
        type: 'text',
        value: searchValue.value,
        onInput: e => {
          handleCalendarSearch(e.target.value)
        },
        autocomplete: 'off',
      }),
    ]
  )

  if (option.key === 'search') {
    return SearchElement
  }

  // User option element
  const userOptionElement = h('div', {}, [
    h(
      'div',
      {
        class:
          'flex p-2 w-full cursor-pointer gap-3 justify-start items-center hover:bg-gray-100',
        onClick: () => handleOptionClick(option),
      },
      [
        h(UICheckbox, {
          class: `text-blue-400`,
          checked: selectedUserIds.value.includes(option.value),
        }),
        h(
          'div',
          {
            class:
              'w-full max-w-full word-break whitespace-normal truncate text-gray-600 truncate-2-lines text-sm',
          },
          {
            default: () => option.label,
          }
        ),
      ]
    ),
    option.value === 'all'
      ? h('div', { class: 'border-b border-gray-200 m-2' })
      : null,
  ])

  // If this is the last user option, append the Edit Hours button after it
  if (isLastOption) {
    const editHoursElement = h(
      'div',
      {
        class:
          'sticky bottom-0 border-t-2 border-gray-200 flex items-center bg-white mt-1',
      },
      [
        h(
          'div',
          {
            class: 'mx-auto p-2 gap-2',
          },
          [
            h(
              UIButton,
              {
                type: 'tertiary',
                size: 'small',
                class: 'flex',
                disabled: selectedUserIds.value.length === 0,
                onClick: handleEditHoursClick,
              },
              [
                h(ClockIcon, { class: 'ml-2 w-4 h-4' }),
                h(
                  'span',
                  { class: 'ml-2' },
                  t('settings.availability.schedule.edit_hours')
                ),
              ]
            ),
          ]
        ),
      ]
    )
    return [userOptionElement, editHoursElement]
  }

  // Otherwise, just return the user option
  return userOptionElement
}

function handleAvatarClick(member) {
  // console.log('handleAvatarClick', member)
  const idx = bulkEditSelectedUsers.value.findIndex(
    x => x.userId === member.userId
  )
  if (idx > -1) {
    bulkEditSelectedUsers.value.splice(idx, 1)
  } else {
    bulkEditSelectedUsers.value.push(member)
  }
  //open the team member panel
  // openTeamMemberPanel(member)
}

async function getAvailabilityTextFromSchedule() {
  if (editingCalendar.value?.isEventCalendar) {
    return
  }
  Object.keys(calendarScheduleState.state.schedules).forEach(userId => {
    //first check if user have associated with the calendar
    let schedule = calendarScheduleState.state.schedules[userId]?.find(x =>
      x?.calendarIds?.includes(editingCalendar.value?.id || '')
    )
    if (!schedule) {
      //get default schedule for the user
      schedule = calendarScheduleState.state.schedules[userId]?.find(
        x => x?.isDefault
      )
    }
    if (schedule) {
      const availabilityText = getAvailabilityText(schedule)
      teamMembersDefaultSchedule.value[userId] = {
        availabilityText,
        schedule,
      }
    }
  })
}

const handleSaveCalendarFromSchedules = () => {
  AppState.isUserAvailabilityUpdated = true
  emit('save-calendar')
}

const getSlotUnavailableHelperText = (option: string) => {
  switch (option) {
    case 'skip':
      return t('calendar_advanced.availability.skip_booking_helper_text')
    case 'continue':
      return t('calendar_advanced.availability.force_book_helper_text')
    case 'book_next':
      return t('calendar_advanced.availability.book_next_available_helper_text')
  }
}
</script>

<template>
  <div
    v-if="editingCalendar?.isEventCalendar"
    class="flex flex-col items-start justify-between gap-2"
  >
    <div class="flex w-full flex-row items-start justify-between">
      <UIHeader
        id="text-only-header"
        :secondary-title="
          $t('settings.availability.my_availability_and_booking_rules')
        "
      >
      </UIHeader>
      <div class="flex flex-row items-center gap-2 pr-2 pt-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          aria-hidden="true"
          class="h-5 w-5 text-gray-500"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 2.458A9.996 9.996 0 0012 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10c0-1.715-.432-3.33-1.193-4.74M17 5.75h.005M10.5 21.888v-2.203a.5.5 0 01.12-.325l2.486-2.9a.5.5 0 00-.131-.76l-2.857-1.632a.499.499 0 01-.186-.187L8.07 10.62a.5.5 0 00-.478-.25l-5.528.492M21 6c0 2.21-2 4-4 6-2-2-4-3.79-4-6a4 4 0 018 0zm-3.75-.25a.25.25 0 11-.5 0 .25.25 0 01.5 0z"
          ></path>
        </svg>
        <UISelect
          id="event-calendar-timezone-select"
          v-model:value="selectedEventTimezone"
          class="w-64"
          :options="computedTimezones"
          :filterable="true"
          :placeholder="
            $t('settings.availability.schedule.timezone_placeholder')
          "
          @update:value="updateEventCalendarTimezone"
        />
      </div>
    </div>
    <div class="-mt-8 w-[50%] text-sm text-gray-500">
      {{
        $t(
          'settings.availability.my_availability_and_booking_rules_description'
        )
      }}
    </div>
  </div>
  <UIHeader
    v-else
    id="text-only-header"
    :secondary-title="
      $t('settings.availability.my_availability_and_booking_rules')
    "
    :description="
      $t('settings.availability.my_availability_and_booking_rules_description')
    "
  />

  <UIDivider />
  <div
    v-if="!editingCalendar?.isEventCalendar"
    class="flex flex-row justify-between"
  >
    <div>
      <p class="text-sm font-semibold">
        {{
          $t('calendar_advanced.availability.scheduling_availability_heading')
        }}
      </p>
    </div>
    <!-- Bulk Edit Button and Dropdown Overlay (right aligned) -->
    <UIDropdown
      v-if="AppState.editCalendarValidationData.teamMembers?.length > 1"
      id="bulk-edit-user-select"
      v-model:value="selectedUserIds"
      class="scrollbar-hide w-full"
      :options="allUsersOptions"
      multiple="true"
      :placeholder="$t('placeholders.select_users')"
      size="small"
      placement="bottom"
      :render-option="renderOption"
      @clickoutside="closeBulkEditDropdown"
      @update:show="showBulkEditDropdown = true"
    >
      <UIButton
        id="bulk-edit-btn"
        type="tertiary"
        size="small"
        class="ml-2 flex items-center gap-2 rounded-lg border bg-primary-50 px-2 text-[13px] font-semibold text-blue-700"
        @click="openBulkEditDropdown"
      >
        <div class="flex flex-row items-center gap-2">
          <Edit01Icon class="h-4 w-4" />
          <span class="text-[13px]">{{
            $t('calendar_advanced.availability.bulk_edit') || 'Bulk Edit'
          }}</span>
        </div>
      </UIButton>
    </UIDropdown>
  </div>

  <div
    v-if="calendarScheduleState.state.initialLoad"
    class="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 bg-white py-3 sm:grid-cols-2"
  >
    <div v-for="i in 2" :key="i" class="w-full">
      <UISkeleton
        height="55px"
        width="100%"
        :sharp="false"
        class="!rounded-lg"
      />
    </div>
  </div>
  <!-- Team Member Bar (non-event calendars only) -->
  <div
    v-if="
      !editingCalendar?.isEventCalendar &&
      firstFourTeamMembers.length > 0 &&
      !calendarScheduleState.state.initialLoad
    "
    class="grid grid-cols-1 gap-x-2 gap-y-3 bg-white py-3 sm:grid-cols-2"
  >
    <button
      v-for="member in firstFourTeamMembers"
      :key="member.userId"
      type="button"
      class="group flex w-full flex-row items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-all duration-150 focus:outline-none"
      :class="[
        bulkEditSelectedUsers.find(x => x?.userId === member?.userId)
          ? 'border-primary-600 shadow-md'
          : 'border-gray-300',
        'hover:border-primary-400 hover:shadow',
      ]"
      :aria-pressed="
        bulkEditSelectedUsers.find(x => x?.userId === member?.userId)
          ? 'true'
          : 'false'
      "
      @click="openTeamMemberPanel(member)"
    >
      <div
        class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
      >
        <img
          v-if="member.profilePhoto"
          :src="member.profilePhoto"
          :alt="member.name"
          class="h-10 w-10 rounded-full object-cover"
        />
        <div v-else class="w-full rounded-full">
          <span class="text-sm font-normal text-gray-700">
            {{ getInitials(member.name) }}
          </span>
        </div>
      </div>
      <div
        class="flex min-w-0 flex-1 flex-col items-start justify-center gap-1"
      >
        <span
          class="w-full truncate text-left text-sm"
          :class="
            bulkEditSelectedUsers.find(x => x.userId === member.userId)
              ? 'font-semibold text-primary-700'
              : 'font-normal text-gray-700'
          "
          :title="member.name"
        >
          {{ member.name }}
        </span>
        <span class="text-left text-xs text-gray-500">
          {{
            teamMembersDefaultSchedule[member.userId]?.availabilityText ||
            t('settings.availability.schedule.no_availability')
          }}
        </span>
      </div>
      <svg
        class="ml-2 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-primary-500"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </div>
  <!-- Bulk Edit Team Members Avatar Group -->
  <TeamMemberAvatarGroup
    v-if="
      !editingCalendar?.isEventCalendar &&
      teamMembersWithFullDetailsMoreThan4.length > 0
    "
    :displayed-members="firstFourTeamMembers"
    :members="teamMembersWithFullDetailsMoreThan4"
    :max-visible="4"
    @edit-hours="handleEditHoursClick"
    @select-user="openTeamMemberPanel"
  />
  <!-- Team Member Side Panel -->
  <TeamMemberSchedulePanel
    :users="bulkEditSelectedUsers"
    :show="showTeamMemberPanel"
    :calendar="(editingCalendar as any)?.data"
    :on-close="closeTeamMemberPanel"
    :on-change-schedule="methods.updateSelectedSchedules"
    @save-calendar="handleSaveCalendarFromSchedules"
  />

  <CustomRecurrenceModal
    :show="state.showCustomRecurrenceModal"
    @on-modal-close="methods.customRecurrenceModalCancel"
    @on-confirm="methods.customRecurrenceModalSave"
  />

  <UIForm
    id="create-calendar-form"
    ref="formRef"
    :model="state.formData"
    :rules="rules"
    class="w-full"
  >
    <section
      v-if="editingCalendar?.isEventCalendar"
      class="flex items-start overflow-hidden rounded-lg"
    >
      <div
        id="availableDaySlotPicker"
        class="max-h-[725px] w-2/3 overflow-y-auto border-r border-gray-200 p-6"
      >
        <div class="mb-4 flex items-center gap-1">
          <p class="text-sm font-semibold">
            {{ $t('calendar_advanced.availability.heading_1') }}
          </p>
          <UITooltip :placement="'top'">
            <template #trigger>
              <InfoCircleIcon class="w-4 text-gray-500" />
            </template>
            <p class="max-w-sm">
              {{ $t('calendar_advanced.availability.tooltip_1') }}
            </p>
          </UITooltip>
        </div>
        <div
          v-for="(availableDay, index) in myAvailability.availableDays.sort(
            (a, b) =>
              daysOfWeekInOrder.indexOf(a.day) -
              daysOfWeekInOrder.indexOf(b.day)
          )"
          :id="'available-day-hours-' + index"
          :key="index"
          class="my-4 flex gap-3 border-b border-gray-200 py-4"
        >
          <UICheckbox
            :id="availableDay.day"
            :key="availableDay.day"
            :name="availableDay.day"
            :checked="availableDay.hours.length > 0"
            class="text-md flex w-20"
            @change="(x: boolean) => myAvailability.updateAvailabilityDay(availableDay, x)
              "
          >
            <div class="font-md mr-32 w-12 font-semibold text-gray-700">
              {{ availableDay?.day?.slice(0, 3) }}
            </div>
          </UICheckbox>

          <div>
            <div
              v-if="availableDay?.hours?.length === 0"
              class="text-sm text-gray-500"
            >
              {{ $t('calendar_advanced.availability.unavailable_label') }}
            </div>
            <div
              v-for="(availableDayHour, hourIndex) in availableDay.hours"
              v-else
              :key="hourIndex"
            >
              <div class="mb-2 flex items-center">
                <UITimePicker
                  v-model:formatted-value="availableDayHour.startTime"
                  value-format="H:m"
                  :minutes="5"
                  format="hh:mm a"
                  use-12-hours
                  :actions="['confirm']"
                />

                <span class="mx-2 font-semibold">{{
                  $t('calendar_advanced.availability.to')
                }}</span>
                <UITimePicker
                  v-model:formatted-value="availableDayHour.endTime"
                  value-format="H:m"
                  :minutes="5"
                  format="hh:mm a"
                  use-12-hours
                  :actions="['confirm']"
                  :is-hour-disabled="
                    hour =>
                      handleIsHourDisabled(hour, availableDayHour.startTime)
                  "
                  :is-minute-disabled="
                    (min, hour) =>
                      handleIsMinDisabled(min, hour, availableDayHour.startTime)
                  "
                />

                <div class="flex w-32 items-center">
                  <UIButton
                    v-if="hourIndex == 0"
                    id="add-availability-hour-std"
                    :aria-label="$t('aria_labels.add_availability_hour')"
                    :text="true"
                    class="ml-4 text-gray-700"
                    :disabled="
                      availableDay.getLastHour().endTime === '0:0' &&
                      availableDay.getLastHour().startTime === '0:0'
                    "
                    @click="
                      x => {
                        const { startTime, endTime } =
                          availableDay.getLastHour()
                        availableDay.addNewAvailabilityHourWithStartTimeAndEndTime(
                          startTime,
                          endTime
                        )
                      }
                    "
                  >
                    <PlusIcon class="w-4" />
                  </UIButton>
                  <UIButton
                    v-if="hourIndex > -1"
                    id="remove-availability-hour-std"
                    :aria-label="$t('aria_labels.remove_availability_hour')"
                    class="ml-4 text-red-600"
                    :text="true"
                    @click="availableDay.removeAvailabilityHour(hourIndex)"
                  >
                    <Trash02Icon class="w-4" />
                  </UIButton>

                  <div class="relative">
                    <UIPopover
                      v-if="hourIndex == 0"
                      trigger="click"
                      :show="activeHourIndexForCopy === index && hourIndex == 0"
                      class="!rounded-md !pb-0"
                      @clickoutside="
                        x => {
                          copyToDays = []
                          activeHourIndexForCopy = -1
                        }
                      "
                    >
                      <template #trigger>
                        <UIButton
                          id="copy-availability-hour-std"
                          :aria-label="$t('aria_labels.copy_availability_hour')"
                          class="ml-4 text-gray-700"
                          :text="true"
                          @click="
                            x => {
                              activeHourIndexForCopy > -1
                                ? (activeHourIndexForCopy = -1)
                                : (activeHourIndexForCopy = index)
                            }
                          "
                        >
                          <Copy06Icon class="mt-[2px] w-4" />
                        </UIButton>
                      </template>
                      <div class="w-40">
                        <p
                          class="text-md mt-0 px-0.5 font-normal text-gray-600"
                        >
                          {{
                            $t('calendar_advanced.availability.copy_times_to')
                          }}...
                        </p>
                        <UICheckbox
                          :id="'All Days'"
                          :key="'All Days'"
                          :name="'All Days'"
                          :checked="copyToDays.length === 7"
                          class="text-md flex w-20 p-2"
                          @change="
                            x =>
                              x
                                ? (copyToDays = [
                                    'Monday',
                                    'Tuesday',
                                    'Wednesday',
                                    'Thursday',
                                    'Friday',
                                    'Saturday',
                                    'Sunday',
                                  ])
                                : (copyToDays = [])
                          "
                        >
                          <div
                            class="font-md mr-32 w-full font-semibold text-gray-700"
                          >
                            {{
                              $t('calendar_advanced.availability.copy_to_all')
                            }}
                          </div>
                        </UICheckbox>
                        <div
                          v-for="(day, k) in MyAvailability.OFFICE_DAYS"
                          :key="k"
                          class="flex items-center p-2"
                        >
                          <UICheckbox
                            :id="day"
                            :key="day"
                            :name="day"
                            :checked="
                              copyToDays.includes(day) ||
                              day === availableDay.day
                            "
                            :disabled="day === availableDay.day"
                            class="text-md flex w-20"
                            @change="
                              x => {
                                x
                                  ? copyToDays.push(day)
                                  : copyToDays.splice(
                                      copyToDays.indexOf(day),
                                      1
                                    )
                              }
                            "
                          >
                            <div
                              class="font-md mr-32 w-full font-semibold text-gray-700"
                            >
                              {{ day }}
                            </div>
                          </UICheckbox>
                        </div>
                        <UIButton
                          id="submit"
                          type="primary"
                          size="small"
                          class="my-2 w-full text-center"
                          @click="
                            x => {
                              myAvailability.copyFromOneToMany(
                                availableDay.day,
                                copyToDays
                              )
                              copyToDays = []
                              activeHourIndexForCopy = -1
                            }
                          "
                        >
                          {{ $t('calendar_advanced.availability.apply') }}
                        </UIButton>
                      </div>
                    </UIPopover>
                  </div>
                </div>
              </div>
              <p
                v-if="availableDay.isOverlapIndex(hourIndex)"
                class="text-left text-sm text-red-600"
              >
                {{ $t('calendar_advanced.availability.overlap_error') }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="max-h-[725px] w-1/3 overflow-y-auto">
        <CustomAvailabilityModal
          ref="customAvailabilityPicker"
          :is-recurring-enabled="state.formData.enableRecurring"
          :toggle-recurring="onRecurringMeetingSwitchChange"
          :schedule="calendarSchedule"
        />
      </div>
    </section>

    <!-- if schedule has availability, show the following -->
    <div v-if="!isAvailableDaysCountZero">
      <UIDivider />
      <!-- look busy switch and input -->
      <div
        v-if="editingCalendar?.calendarType !== CalendarType.CLASS_BOOKING"
        class="flex gap-20 pb-3 pt-4"
      >
        <div class="flex h-full items-center space-x-3 p-0">
          <div id="calendar-lookBusy-switch">
            <UISwitch
              v-model:value="state.formData.lookBusyConfig.enabled"
              :aria-label="t('calendar_advanced.availability.look_busy_title')"
              :title="t('calendar_advanced.availability.look_busy_title')"
            />
          </div>
          <div class="flex items-center space-x-3">
            <div class="font-normal">
              {{ $t('calendar_advanced.availability.look_busy_title') }}
            </div>
            <UITooltip :placement="'top'">
              <template #trigger>
                <InfoCircleIcon class="w-4 text-gray-500" />
              </template>
              <p class="max-w-sm">
                {{ t('calendar_advanced.availability.look_busy_helper_text') }}
              </p>
            </UITooltip>
          </div>
        </div>
        <div>
          <UIInputNumber
            :id="'calendar-lookBusy-value'"
            v-model="state.formData.lookBusyConfig.lookBusyPercentage"
            :placeholder="''"
            :readonly="false"
            :loading="false"
            :clearable="false"
            :disabled="!state.formData.lookBusyConfig.enabled"
            :precision="undefined"
            :size="'medium'"
            :max="'100'"
            :min="'0'"
            :show-button="false"
          >
            <template #suffix>%</template>
          </UIInputNumber>
          <div
            class="leading-20 pt-2 text-left text-sm font-normal text-gray-500"
          >
            {{
              t(
                'calendar_advanced.availability.look_busy_percentage_description'
              )
            }}
          </div>
        </div>
      </div>

      <!-- recurring meeting switch -->

      <div v-if="!isServiceCalendar && !isCollectiveCalendar">
        <div
          v-if="!isRecurringMeetingSwitchDisabled"
          class="flex items-center space-x-3 pb-3 pt-4"
        >
          <div id="calendar-recurringMeeting-switch">
            <UISwitch
              v-model:value="state.formData.enableRecurring"
              :aria-label="
                t('calendar_advanced.availability.recurring_meeting')
              "
              :disabled="isRecurringMeetingSwitchDisabled"
              @update:value="onRecurringMeetingSwitchChange"
            />
          </div>
          <div class="flex flex-row items-center space-x-3">
            <div class="font-normal">
              {{ $t('calendar_advanced.availability.recurring_meeting') }}
            </div>
            <UITooltip :placement="'top'">
              <template #trigger>
                <InfoCircleIcon class="w-4 text-gray-500" />
              </template>
              <p class="max-w-sm">
                {{
                  $t('calendar_advanced.availability.recurring_meeting_tooltip')
                }}
              </p>
            </UITooltip>
          </div>
        </div>
        <div v-else-if="isRecurringMeetingSwitchDisabled" class="w-[250px]">
          <UITooltip placement="top">
            <template #trigger>
              <div class="flex items-center space-x-3 pb-3 pt-4">
                <UISwitch
                  id="calendar-recurringMeeting-switch"
                  v-model:value="state.formData.enableRecurring"
                  :disabled="true"
                  :aria-label="
                    t('calendar_advanced.availability.recurring_meeting')
                  "
                />
                <div class="flex items-center space-x-3">
                  <div class="font-normal">
                    {{ $t('calendar_advanced.availability.recurring_meeting') }}
                  </div>
                  <UITooltip :placement="'top'">
                    <template #trigger>
                      <InfoCircleIcon class="w-4 text-gray-500" />
                    </template>
                    <p class="max-w-sm">
                      {{ recurringMeetingSwitchTooltip }}
                    </p>
                  </UITooltip>
                </div>
              </div>
            </template>
            <p class="max-w-sm">
              {{ recurringMeetingSwitchTooltip }}
            </p>
          </UITooltip>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- Look busy for unavailable days -->
      <div v-if="editingCalendar?.calendarType !== CalendarType.CLASS_BOOKING">
        <UIDivider />
        <div class="flex gap-20 pb-3 pt-4">
          <div class="flex h-full items-center space-x-3 p-0">
            <div id="calendar-lookBusy-switch">
              <UISwitch
                v-model:value="state.formData.lookBusyConfig.enabled"
                :aria-label="
                  t('calendar_advanced.availability.look_busy_title')
                "
              />
            </div>
            <div class="flex items-center space-x-3">
              <div class="font-normal">
                {{ $t('calendar_advanced.availability.look_busy_title') }}
              </div>
              <UITooltip :placement="'top'">
                <template #trigger>
                  <InfoCircleIcon class="w-4 text-gray-500" />
                </template>
                <p class="max-w-sm">
                  {{
                    t('calendar_advanced.availability.look_busy_helper_text')
                  }}
                </p>
              </UITooltip>
            </div>
          </div>
          <div>
            <UIInputNumber
              :id="'calendar-lookBusy-value'"
              v-model="state.formData.lookBusyConfig.lookBusyPercentage"
              :placeholder="''"
              :readonly="false"
              :loading="false"
              :clearable="false"
              :disabled="!state.formData.lookBusyConfig.enabled"
              :precision="undefined"
              :size="'medium'"
              :max="'100'"
              :min="'0'"
              :show-button="false"
            >
              <template #suffix>%</template>
            </UIInputNumber>
            <div
              class="leading-20 pt-2 text-left text-sm font-normal text-gray-500"
            >
              {{
                t(
                  'calendar_advanced.availability.look_busy_percentage_description'
                )
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- recurring settings -->
    <div
      v-if="state.formData.enableRecurring && !isAvailableDaysCountZero"
      class="mt-6"
    >
      <div class="flex items-start space-x-9">
        <UIFormItem
          :label="$t('calendar_advanced.availability.repeat_frequency')"
          path="recurring.freq"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.availability.repeat_frequency_tooltip')
            )
          "
        >
          <div class="flex flex-col space-y-1">
            <UISelect
              id="calendar-recurrence-repeat"
              :value="
                state.isCustomRecurring
                  ? 'CUSTOM'
                  : state.formData.recurring.freq
              "
              class="w-64"
              :options="recurringFrequencyOptions"
              @update:value="methods.updateFrequency"
            />

            <div class="w-64">
              <div
                v-if="state.isCustomRecurring"
                class="flex items-center space-x-2 capitalize text-gray-400"
              >
                <div class="">
                  {{ state.rruleText }}
                </div>
                <XIcon
                  class="h-5 w-5 flex-grow cursor-pointer"
                  :aria-label="$t('aria_labels.update_frequency')"
                  @click="() => methods.updateFrequency('DAILY')"
                />
              </div>
              <div v-else class="capitalize text-gray-400">
                {{ state.rruleText }}
              </div>
            </div>
          </div>
        </UIFormItem>

        <UIFormItem
          :label="$t('calendar_advanced.availability.times_to_repeat')"
          path="recurringCount"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.availability.times_to_repeat_tooltip')
            )
          "
        >
          <UIInputNumber
            id="calendar-recurrence-count"
            v-model="state.formData.recurring.count"
            class="w-64"
          />
        </UIFormItem>
      </div>

      <div class="flex space-x-9">
        <UIFormItem
          :label="$t('calendar_advanced.availability.slots_unavailable')"
          path="recurring.bookingOption"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.availability.slots_unavailable_tooltip')
            )
          "
        >
          <div class="flex flex-col items-start">
            <UISelect
              id="calendar-recurrence-recurrenceFreq"
              v-model:value="state.formData.recurring.bookingOption"
              class="w-64"
              :options="recurringBookingOptions"
            />
            <div
              class="leading-20 w-64 pt-2 text-left text-sm font-normal text-gray-500"
            >
              {{
                getSlotUnavailableHelperText(
                  state.formData.recurring.bookingOption
                )
              }}
            </div>
          </div>
        </UIFormItem>

        <UIFormItem
          v-if="state.formData.recurring.bookingOption === 'continue'"
          :label="$t('calendar_advanced.availability.overlap_status')"
          path="recurring.bookingOverlapDefaultStatus"
          class="h-min"
        >
          <UISelect
            id="calendar-bookingOverlapDefaultStatus"
            v-model:value="state.formData.recurring.bookingOverlapDefaultStatus"
            class="w-80"
            :options="recurringOverlapStatusOptions"
          />
        </UIFormItem>
      </div>
    </div>

    <div class="flex space-x-8">
      <DurationPicker
        :label="
          isServiceCalendar
            ? $t('calendar_advanced.availability.service_interval')
            : $t('calendar_advanced.availability.meeting_interval')
        "
        :tooltip="
          isServiceCalendar
            ? $t('calendar_advanced.availability.service_interval_tooltip')
            : $t('calendar_advanced.availability.meeting_interval_tooltip')
        "
        path="slotInterval"
        :value="state.formData.slotInterval"
        :unit="state.formData.slotIntervalUnit"
        @on-value-change="
          value => {
            state.formData.slotInterval = value.duration
            state.formData.slotIntervalUnit = value.timeUnit
          }
        "
      />
      <DurationPicker
        :label="
          isServiceCalendar
            ? $t('calendar_advanced.availability.service_duration')
            : $t('calendar_advanced.availability.meeting_duration')
        "
        :tooltip="
          isServiceCalendar
            ? $t('calendar_advanced.availability.service_duration_tooltip')
            : $t('calendar_advanced.availability.meeting_duration_tooltip')
        "
        path="slotDuration"
        :value="state.formData.slotDuration"
        :unit="state.formData.slotDurationUnit"
        @on-value-change="
          value => {
            state.formData.slotDuration = value.duration
            state.formData.slotDurationUnit = value.timeUnit
          }
        "
      />
    </div>

    <div class="flex space-x-8">
      <DurationPicker
        :label="$t('calendar_advanced.availability.minimum_scheduling')"
        :tooltip="
          isServiceCalendar
            ? $t('calendar_advanced.availability.minimum_scheduling_tooltip')
            : $t(
                'calendar_advanced.availability.minimum_scheduling_notice_tooltip'
              )
        "
        path="allowBookingAfter"
        :value="state.formData.allowBookingAfter"
        :unit="state.formData.allowBookingAfterUnit"
        @on-value-change="
          value => {
            state.formData.allowBookingAfter = value.duration
            state.formData.allowBookingAfterUnit = value.timeUnit
          }
        "
      />
      <DurationPicker
        :label="$t('calendar_advanced.availability.date_range')"
        :tooltip="
          $t('calendar_advanced.availability.date_range_preferences_tooltip')
        "
        path="allowBookingFor"
        :value="state.formData.allowBookingFor"
        :unit="state.formData.allowBookingForUnit"
        @on-value-change="
          value => {
            state.formData.allowBookingFor = value.duration
            state.formData.allowBookingForUnit = value.timeUnit
          }
        "
      />
    </div>
    <div
      v-if="
        ![CalendarType.CLASS_BOOKING, CalendarType.SERVICE].includes(
          editingCalendar?.calendarType as CalendarType
        )
      "
      class="mt-4 flex items-end space-x-8"
    >
      <div class="w-1/2">
        <UIFormItem
          :label="$t('calendar_advanced.availability.maximum_bookings_per_day')"
          path="appoinmentPerDay"
          :tooltip="
            getTooltip(
              $t(
                'calendar_advanced.availability.maximum_bookings_per_day_tooltip'
              )
            )
          "
        >
          <UIInputNumber
            id="calendar-appointments-perDay"
            v-model="state.formData.appoinmentPerDay"
            class="w-full"
            placeholder=""
          />
        </UIFormItem>
      </div>
      <div v-if="!isServiceCalendar && !isCollectiveCalendar" class="w-1/2">
        <UIFormItem
          :label="
            editingCalendar?.calendarType === CalendarType.ROUND_ROBIN ||
            editingCalendar?.calendarType === CalendarType.PERSONAL
              ? $t(
                  'calendar_advanced.availability.maximum_bookings_per_slot_per_user'
                )
              : $t('calendar_advanced.availability.maximum_bookings_per_slot')
          "
          path="appoinmentPerSlot"
          :tooltip="
            getTooltip(
              $t(
                editingCalendar?.calendarType === CalendarType.PERSONAL
                  ? 'calendar_advanced.availability.maximum_bookings_per_slot_tooltip_personal'
                  : 'calendar_advanced.availability.maximum_bookings_per_slot_tooltip'
              )
            )
          "
        >
          <UIInputNumber
            id="calendar-appointments-perSlot"
            v-model="state.formData.appoinmentPerSlot"
            class="w-full"
            placeholder=""
          />
        </UIFormItem>
      </div>
    </div>

    <UIForm v-if="isServiceCalendar" class="flex w-1/2 space-x-8">
      <UIFormItem
        class="mt-4 w-full"
        :label="$t('calendar_advanced.availability.post_buffer_time')"
        path="slotBuffer"
      >
        <UISelect
          id="calendar-slot-buffer"
          :value="`${state.formData.slotBuffer || 0} mins`"
          class="w-full"
          :options="bufferOptionForServices"
          @update:value="state.formData.slotBuffer = $event"
        />
      </UIFormItem>
    </UIForm>

    <div class="flex items-end space-x-8">
      <div
        v-if="editingCalendar?.calendarType === CalendarType.CLASS_BOOKING"
        class="w-1/2"
      >
        <UIFormItem
          :label="$t('calendar_advanced.availability.seats_per_class')"
          path="appoinmentPerSlot"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.availability.seats_per_class_tooltip')
            )
          "
        >
          <UIInputNumber
            id="calendar-appointments-per-seat"
            v-model="state.formData.appoinmentPerSlot"
            class="w-full"
            placeholder=""
          />
        </UIFormItem>
      </div>
      <div class="w-1/2">
        <DurationPicker
          v-if="!isServiceCalendar"
          :label="$t('calendar_advanced.availability.pre_buffer_time')"
          :tooltip="
            $t('calendar_advanced.availability.pre_buffer_time_tooltip')
          "
          path="preBuffer"
          :value="state.formData.preBuffer"
          :unit="state.formData.preBufferUnit"
          @on-value-change="
            value => {
              state.formData.preBuffer = value.duration
              state.formData.preBufferUnit = value.timeUnit
            }
          "
        />
      </div>
      <div class="w-1/2">
        <DurationPicker
          v-if="!isServiceCalendar"
          :tooltip="
            $t('calendar_advanced.availability.post_buffer_time_tooltip')
          "
          :label="$t('calendar_advanced.availability.post_buffer_time')"
          path="slotBuffer"
          :value="state.formData.slotBuffer"
          :unit="state.formData.slotBufferUnit"
          @on-value-change="
            value => {
              state.formData.slotBuffer = value.duration
              state.formData.slotBufferUnit = value.timeUnit
            }
          "
        />
      </div>
    </div>

    <DependentSettings
      v-if="state.showDependentSettings"
      :show-modal="state.showDependentSettings"
      :desc="t('calendar_advanced.availability.enable_recurring_desc')"
      :title="t('calendar_advanced.availability.enable_recurring_title')"
      :settings="recurringDependentSettings"
      :is-save-button-disabled="dependentSettingsConfirmSaveButtonDisabled"
      @confirm-click="onClickConfirm"
      @cancel-click="onClickCancel"
    >
      <template #body>
        <div
          v-if="AppState.editCalendarValidationData.teamMembers.length > 1"
          class="mb-[20px] mt-[15px]"
        >
          <div class="mb-[10px] text-[14px] font-medium text-gray-700">
            {{ t('calendar_advanced.availability.select_team_member_to_keep') }}
            <span class="text-red-500">*</span>
          </div>
          <UISelect
            id="calendar-select-team-member"
            v-model:value="state.recurrTeamMembers"
            :options="usersInLocationOptions"
            :filterable="true"
            :render-label="renderLabel"
            :placeholder="
              t('calendar_advanced.availability.select_team_member')
            "
            :tooltip="
              getTooltip(
                $t('calendar_advanced.availability.select_team_member_tooltip')
              )
            "
          />
        </div>

        <div
          v-if="
            AppState.shouldAcceptPayment && AppState.isPartialPaymentChecked
          "
          class="mb-[20px] mt-[20px]"
        >
          <div class="mb-[3px] text-[14px] font-medium text-gray-700">
            {{ t('calendar_advanced.availability.payment_preference') }}
          </div>
          <div
            class="mb-[10px] text-[14px] font-medium text-gray-500"
            v-html="
              $t('calendar_advanced.availability.partial_payments_disabled')
            "
          />

          <UISelect
            id="calendar-select-recurring-appointment-payment"
            v-model:value="state.recurringPaymentType"
            class="w-full"
            :options="recurringAppointmentPaymentOptions(t)"
          />
        </div>
      </template>
    </DependentSettings>
  </UIForm>
</template>

<style scoped>
@keyframes slide-in {
  from {
    right: -400px;
  }
  to {
    right: 0;
  }
}
.animate-slide-in {
  animation: slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scrollbar-hide {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
<style>
#bulk-edit-user-select {
  min-height: 200px !important;
  max-height: 300px !important;
  width: 220px !important;
  min-width: 220px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  padding-top: 0px !important;
  overscroll-behavior: contain;
  padding-bottom: 0px !important;
  border-radius: 8px !important;
  border: 1px solid theme('colors.gray.200') !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
#bulk-edit-user-select::-webkit-scrollbar {
  display: none;
}
.truncate-2-lines {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  align-items: start;
}
</style>
